// DEPENDENCIES
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useFormik } from 'formik';
// COMPONENTS
import MUIDataTable from 'mui-datatables';
import { Button, Modal, Form, Spinner, ListGroup } from 'react-bootstrap';
import SVG from 'react-inlinesvg';
// CUSTOM COMPONENTS
import * as alert from '../../../redux/alertSnackBarRedux';
import * as confirmModal from '../../../redux/confirmModalRedux';
// SERVICES AND HELPERS
import * as projectTypeService from '../../../services/companyData/projectTypeService';
import ProjectTypeValidator from '../../../helpers/validators/ProjectTypeValidator';
import { getInputClasses } from '../../../helpers/helper';
import { toAbsoluteUrl } from '../../../_metronic/_helpers';

var initialProjectType = {
  id: 0,
  name: '',
  description: '',
  projectSubTypes: [],
};

const ProjectTypesListingPage = (props) => {
  const { showAlert, showConfirmModal, hideConfirmModal, setProcessing } = props;
  const [data, setData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProjectType, setSelectedProjectType] = useState(initialProjectType);
  const [selectedProjectSubTypes, setSelectedProjectSubTypes] = useState([]);
  const [projectSubTypeList, setProjectSubTypeList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTableData();
  }, []);

  const loadTableData = () => {
    projectTypeService
      .getProjectTypes()
      .then((res) => {
        setData(res);
      })
      .catch((ex) => {
        showAlert({ message: ex.message, variant: 'error' });
      })
      .finally((x) => {
        setLoading(false);
      });
  };

  const editProjectType = (id) => {
    projectTypeService.getProjectTypeById(id).then((res) => {
      setSelectedProjectType(res);
      setProjectSubTypeList(res.projectSubTypes);
      setIsModalOpen(true);
    });
  };

  const deleteProjectType = (id) => {
    setProcessing(true);
    projectTypeService.deleteProjectType(id).then((res) => {
      showAlert({ message: res.message, variant: 'success' });
      loadTableData();
      hideConfirmModal();
    });
  };

  const columns = [
    {
      name: 'id',
      label: 'Actions',
      options: {
        filter: false,
        sort: false,
        width: 1,
        download: false,
        setCellProps: () => ({ 'data-aes-field': 'actions' }),
        setCellHeaderProps: () => ({ 'data-aes-field': 'actions' }),
        customBodyRenderLite: (dataIndex) => {
          return (
            <div className='aes-admin-listing-actions'>
              <button
                className='btn btn-sm btn-info font-weight-bold py-2 px-3 px-xxl-5'
                onClick={() => editProjectType(data[dataIndex].id)}
              >
                Edit
              </button>
              <button
                onClick={() =>
                  showConfirmModal({
                    title: 'Delete Project Type',
                    text:
                      'Are you sure you want to delete this project type?  <b>Deleting project type will delete all the project sub types and projects related to this project type. Please make sure before you submit </b>',
                    rightBtnText: 'Confirm',
                    btnAction: () => deleteProjectType(data[dataIndex].id),
                  })
                }
                className='btn btn-sm btn-danger font-weight-bold py-2 px-3 px-xxl-5 my-1'
              >
                Delete
              </button>
            </div>
          );
        },
      },
    },
    {
      name: 'name',
      label: 'Name',
      options: {
        filter: true,
        sort: true,
        setCellProps: () => ({ 'data-aes-field': 'primary', 'data-aes-label': 'Name' }),
        setCellHeaderProps: () => ({ 'data-aes-field': 'primary' }),
      },
    },
    {
      name: 'description',
      label: 'Description',
      options: {
        filter: true,
        sort: true,
        setCellProps: () => ({ 'data-aes-field': 'detail', 'data-aes-label': 'Description' }),
        setCellHeaderProps: () => ({ 'data-aes-field': 'detail' }),
        customBodyRenderLite: (dataIndex) => {
          return <>{data[dataIndex].description ? data[dataIndex].description : '-'}</>;
        },
      },
    },
  ];

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedProjectType(initialProjectType);
    formik.resetForm();
    setSelectedProjectSubTypes([]);
  };

  const saveProjectType = (values, setStatus, setSubmitting) => {
    let submissionModel = values;
    submissionModel.projectSubTypes = selectedProjectSubTypes.map((x) => {
      return { projectTypeId: values.id, projectSubTypeId: x.value };
    });

    projectTypeService
      .saveProjectType(submissionModel)
      .then((res) => {
        showAlert({ message: res.message, variant: 'success' });
        loadTableData();
        handleModalClose();
      })
      .catch((ex) => {
        showAlert({ message: ex.message, variant: 'error' });
      });
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: selectedProjectType,
    validationSchema: ProjectTypeValidator,
    onSubmit: (values, { setStatus, setSubmitting }) => {
      saveProjectType(values, setStatus, setSubmitting);
    },
  });

  return (
    <div className='row aes-admin-listing-page aes-admin-listing-page--project-types'>
      <Modal centered size='lg' show={isModalOpen} onHide={handleModalClose}>
        <form className='card card-custom' onSubmit={formik.handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Project Type</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className='form'>
              <div className='form-group row'>
                <label className='col-xl-3 col-lg-3 col-form-label col-xl-3 col-lg-3 col-form-label fz-mdsm fw-600'>Name</label>
                <div className='col-lg-9 col-xl-9'>
                  <div className='input-group input-group-lg input-group-solid'>
                    <input
                      mask='999-99-9999'
                      placeholder='Start typing...'
                      className={`form-control form-control-lg form-control-solid ${getInputClasses(formik, 'name')}`}
                      name='name'
                      {...formik.getFieldProps('name')}
                    />
                  </div>
                  {formik.touched.name && formik.errors.name ? (
                    <div className='invalid-feedback display-block'>{formik.errors.name}</div>
                  ) : null}
                </div>
              </div>
              <div className='form-group row'>
                <label className='col-xl-3 col-lg-3 col-form-label col-xl-3 col-lg-3 col-form-label fz-mdsm fw-600'>Description</label>
                <div className='col-lg-9 col-xl-9'>
                  <div className='input-group input-group-lg input-group-solid'>
                    <Form.Control
                      as='textarea'
                      rows='5'
                      placeholder='Start typing...'
                      className={`form-control form-control-lg form-control-solid ${getInputClasses(formik, 'description')}`}
                      name='description'
                      {...formik.getFieldProps('description')}
                    />
                  </div>
                  {formik.touched.description && formik.errors.description ? (
                    <div className='invalid-feedback display-block'>{formik.errors.description}</div>
                  ) : null}
                </div>
              </div>
              {selectedProjectType.id > 0 && (
                <div className='form-group row'>
                  <label className='col-xl-3 col-lg-3 col-form-label col-xl-3 col-lg-3 col-form-label fz-mdsm fw-600'>
                    Project Sub Types
                  </label>
                  <div className='col-lg-9 col-xl-9' style={{ marginTop: '10px' }}>
                    {projectSubTypeList.length > 0 ? (
                      projectSubTypeList.map((x, index) => (
                        <ListGroup.Item key={x.id} className='d-flex justify-content-between align-items-center'>
                          {x.name}
                        </ListGroup.Item>
                      ))
                    ) : (
                      <p>No SubType has been assigned for this project</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant='secondary' onClick={handleModalClose}>
              Close
            </Button>
            <button type='submit' className='btn btn-success mr-2' disabled={formik.isSubmitting || (formik.touched && !formik.isValid)}>
              Save
              {formik.isSubmitting}
            </button>
          </Modal.Footer>
        </form>
      </Modal>
      <div className='col-lg-12 col-xxl-12'>
        <div className={`card card-custom card-stretch gutter-b}`}>
          <div className='card-header border-0 py-5'>
            <h3 className='card-title align-items-start flex-column'>
              <span className='card-label font-weight-bolder text-dark'>Project Types</span>
              <span className='text-muted mt-3 font-weight-bold fz-xs'>View and Manage Project Types</span>
            </h3>
            <div className='card-toolbar'>
              <button onClick={() => setIsModalOpen(true)} className='btn btn-success font-weight-bolder font-size-sm mr-3'>
                <span className='svg-icon svg-icon-lg svg-icon-white'>
                  <SVG src={toAbsoluteUrl('/media/svg/icons/Code/Plus.svg')} />
                </span>{' '}
                Add Project Type
              </button>
            </div>
          </div>
          <div className='container-fluid aes-admin-listing p-0'>
            <MUIDataTable
              className='aes-admin-listing-surface'
              data={data}
              columns={columns}
              options={{
                responsive: 'standard',
                rowsPerPage: 25,
                rowsPerPageOptions: [10, 15, 20, 25, 30, 35, 40, 45, 50, 100],
                selectableRows: 'none',
                setTableProps: () => ({
                  className: 'aes-admin-listing-table',
                }),
                textLabels: {
                  body: {
                    noMatch: loading ? <Spinner animation='border' size='lg' variant='info' /> : 'Sorry, no matching records found.',
                  },
                },
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default connect(null, { ...alert.actions, ...confirmModal.actions })(ProjectTypesListingPage);
